import React, { useState } from 'react';
import { 
  Sparkles, Send, Loader2, FileText, TrendingUp, Lightbulb, 
  AlertTriangle, RefreshCw, Copy, CheckCircle, Scale
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { chatLegalAPI } from '../api';

/**
 * Componente de Análisis IA para Expedientes
 * Se integra dentro de la vista de detalle del expediente
 */
const AnalisisIAExpediente = ({ expediente, actuaciones = [] }) => {
  const [pregunta, setPregunta] = useState('');
  const [respuesta, setRespuesta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analisisActivo, setAnalisisActivo] = useState(null);

  // Tipos de análisis predefinidos
  const tiposAnalisis = [
    {
      id: 'resumen',
      titulo: 'Resumen del Expediente',
      descripcion: 'Genera un resumen completo del estado actual',
      icon: FileText,
      color: 'bg-blue-100 text-blue-700 hover:bg-blue-200'
    },
    {
      id: 'probabilidad',
      titulo: 'Probabilidad de Éxito',
      descripcion: 'Analiza las posibilidades de éxito del caso',
      icon: TrendingUp,
      color: 'bg-green-100 text-green-700 hover:bg-green-200'
    },
    {
      id: 'estrategia',
      titulo: 'Sugerir Estrategia',
      descripcion: 'Propone estrategias legales basadas en el expediente',
      icon: Lightbulb,
      color: 'bg-purple-100 text-purple-700 hover:bg-purple-200'
    },
    {
      id: 'riesgos',
      titulo: 'Análisis de Riesgos',
      descripcion: 'Identifica posibles riesgos y puntos débiles',
      icon: AlertTriangle,
      color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
    }
  ];

  // Construir contexto del expediente para la IA
  const construirContexto = () => {
    let contexto = `
EXPEDIENTE: ${expediente.numero_expediente}
TÍTULO: ${expediente.titulo || 'No especificado'}
TIPO: ${expediente.tipo_procedimiento || expediente.tipo || 'No especificado'}
RAMA: ${expediente.rama || 'No especificada'}
ESTADO: ${expediente.estado || 'activo'}
FASE: ${expediente.fase || 'No especificada'}
JUZGADO: ${expediente.juzgado || expediente.juzgado_nombre || 'No especificado'}
FECHA INICIO: ${expediente.fecha_inicio || 'No especificada'}
VALOR ECONÓMICO: ${expediente.valor_economico ? `${expediente.valor_economico.toLocaleString('es-ES')} €` : 'No especificado'}
DESCRIPCIÓN: ${expediente.descripcion || 'Sin descripción'}
CONTRARIO: ${expediente.contrario || 'No especificado'}
PROCURADOR: ${expediente.procurador || 'No asignado'}
`;

    if (actuaciones && actuaciones.length > 0) {
      contexto += '\n\nACTUACIONES DEL EXPEDIENTE:\n';
      actuaciones.forEach((act, idx) => {
        contexto += `${idx + 1}. [${act.fecha || 'Sin fecha'}] ${act.tipo || act.titulo}: ${act.descripcion || act.observaciones || 'Sin descripción'}\n`;
      });
    }

    return contexto;
  };

  // Ejecutar análisis predefinido
  const ejecutarAnalisis = async (tipo) => {
    setLoading(true);
    setAnalisisActivo(tipo);
    setRespuesta(null);

    try {
      let response;
      
      switch (tipo) {
        case 'resumen':
          response = await chatLegalAPI.resumirActuaciones(expediente.id);
          break;
        case 'probabilidad':
          response = await chatLegalAPI.analizarProbabilidad(expediente.id);
          break;
        case 'estrategia':
          response = await chatLegalAPI.sugerirEstrategia(expediente.id);
          break;
        case 'riesgos':
          // Para riesgos, usamos el análisis general con contexto específico
          response = await chatLegalAPI.analizar({
            expediente_id: expediente.id,
            pregunta: 'Analiza los posibles riesgos, puntos débiles y aspectos que podrían perjudicar este caso. Identifica las vulnerabilidades procesales y sustantivas.',
            tipo_analisis: 'riesgos'
          });
          break;
        default:
          throw new Error('Tipo de análisis no válido');
      }

      setRespuesta({
        tipo,
        contenido: response.data.analisis || response.data.resumen || response.data.estrategia || response.data.response || 'Sin respuesta',
        fecha: new Date().toLocaleString('es-ES')
      });
    } catch (error) {
      console.error('Error en análisis:', error);
      toast({
        title: 'Error en análisis',
        description: error.response?.data?.detail || 'No se pudo completar el análisis',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Enviar pregunta personalizada
  const enviarPregunta = async () => {
    if (!pregunta.trim()) return;

    setLoading(true);
    setAnalisisActivo('custom');
    setRespuesta(null);

    try {
      const contexto = construirContexto();
      const mensajeCompleto = `
Contexto del expediente:
${contexto}

Pregunta del usuario: ${pregunta}

Por favor, responde basándote en la información del expediente proporcionada.
`;

      const response = await chatLegalAPI.analizar({
        expediente_id: expediente.id,
        pregunta: mensajeCompleto,
        tipo_analisis: 'consulta_general'
      });

      setRespuesta({
        tipo: 'custom',
        pregunta: pregunta,
        contenido: response.data.analisis || response.data.response || 'Sin respuesta',
        fecha: new Date().toLocaleString('es-ES')
      });
      setPregunta('');
    } catch (error) {
      console.error('Error en consulta:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo procesar la consulta',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const copiarRespuesta = () => {
    if (respuesta?.contenido) {
      navigator.clipboard.writeText(respuesta.contenido);
      toast({ title: 'Copiado al portapapeles' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Análisis con Inteligencia Artificial</h3>
            <p className="text-sm text-gray-500">Analiza el expediente con IA especializada en derecho</p>
          </div>
        </div>
        <Badge variant="outline" className="bg-purple-50">
          <Scale className="w-3 h-3 mr-1" /> Asistente Legal
        </Badge>
      </div>

      {/* Botones de análisis predefinidos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {tiposAnalisis.map((tipo) => (
          <Button
            key={tipo.id}
            variant="ghost"
            onClick={() => ejecutarAnalisis(tipo.id)}
            disabled={loading}
            className={`h-auto py-4 px-3 flex flex-col items-center gap-2 ${tipo.color} ${
              analisisActivo === tipo.id && loading ? 'ring-2 ring-offset-2 ring-blue-500' : ''
            }`}
          >
            {loading && analisisActivo === tipo.id ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <tipo.icon className="w-5 h-5" />
            )}
            <span className="text-xs font-medium text-center">{tipo.titulo}</span>
          </Button>
        ))}
      </div>

      {/* Pregunta personalizada */}
      <Card className="border-dashed">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Textarea
              placeholder="Haz una pregunta específica sobre este expediente..."
              value={pregunta}
              onChange={(e) => setPregunta(e.target.value)}
              className="flex-1 min-h-[80px]"
              disabled={loading}
            />
            <Button
              onClick={enviarPregunta}
              disabled={loading || !pregunta.trim()}
              className="bg-blue-600 hover:bg-blue-700 self-end"
            >
              {loading && analisisActivo === 'custom' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Ejemplo: "¿Cuáles son los plazos que debo tener en cuenta?" o "Resume las actuaciones más importantes"
          </p>
        </CardContent>
      </Card>

      {/* Resultado del análisis */}
      {respuesta && (
        <Card className="bg-gradient-to-br from-gray-50 to-white border-l-4 border-l-purple-500">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <CardTitle className="text-base">
                  {respuesta.tipo === 'custom' ? 'Respuesta a tu consulta' : 
                   tiposAnalisis.find(t => t.id === respuesta.tipo)?.titulo || 'Análisis'}
                </CardTitle>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{respuesta.fecha}</span>
                <Button variant="ghost" size="sm" onClick={copiarRespuesta}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            {respuesta.pregunta && (
              <CardDescription className="text-sm italic">
                "{respuesta.pregunta}"
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
                {respuesta.contenido}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Estado vacío */}
      {!respuesta && !loading && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
          <Sparkles className="w-10 h-10 mx-auto text-purple-300 mb-3" />
          <p className="text-gray-600 font-medium">Selecciona un tipo de análisis</p>
          <p className="text-sm text-gray-500 mt-1">
            O escribe una pregunta personalizada sobre este expediente
          </p>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="text-center py-8 bg-purple-50 rounded-lg">
          <Loader2 className="w-8 h-8 mx-auto text-purple-600 animate-spin mb-3" />
          <p className="text-purple-700 font-medium">Analizando expediente...</p>
          <p className="text-sm text-purple-500 mt-1">
            La IA está procesando la información
          </p>
        </div>
      )}
    </div>
  );
};

export default AnalisisIAExpediente;
